import { create } from "zustand";
import { AuthResponse, LoginAuthResponse } from "../models/AuthResponse";
import User from "../models/User";
import { clearPersistedCache, queryClient } from "../networking/api-hooks";
import { USER_TYPE } from "../configs/constants/UserTypes";
import { apiStore } from "./api.store";

const STORAGE_KEY_ACCESS_TOKEN = "accessToken";
const STORAGE_KEY_REFRESH_TOKEN = "refreshToken";
const STORAGE_KEY_USER_ID = "userId";
const STORAGE_KEY_ORGANISATION_ID = "orgId";
const STORAGE_KEY_USER_NAME = "loggedInUserName";
const STORAGE_KEY_SCOPES = "scopes";

/**
 * Set when the session ended because the refresh token was refused, read by the
 * login page to explain why the user is there. Lives in sessionStorage (per tab)
 * so it survives the `localStorage.clear()` in `onLogoutCleanup`.
 */
export const SESSION_STORAGE_KEY_SESSION_EXPIRED = "sessionExpired";

/** Refresh-token errors that mean the session is over (auth-service 420.1 / 404). */
const SESSION_ENDED_ERROR_CODES = ["auth-service.420.1", "auth-service.404.1"];

/** Whether the login page should say "Din session har gått ut". */
export function hasSessionExpiredNotice(): boolean {
	try {
		return sessionStorage.getItem(SESSION_STORAGE_KEY_SESSION_EXPIRED) !== null;
	} catch {
		return false;
	}
}

export function clearSessionExpiredNotice() {
	try {
		sessionStorage.removeItem(SESSION_STORAGE_KEY_SESSION_EXPIRED);
	} catch {
		// Storage unavailable: nothing to clear.
	}
}

/**
 * Remembers that the session expired. The value is the auth-service error code
 * (e.g. `auth-service.420.1` REFRESH_TOKEN_EXPIRED) or `refresh-failed` for any
 * other failure, for debugging; the notice is the same either way.
 */
function markSessionExpired(error: unknown) {
	const code = (error as { error?: { code?: unknown } } | undefined)?.error?.code;
	const reason =
		typeof code === "string" && SESSION_ENDED_ERROR_CODES.includes(code) ? code : "refresh-failed";

	try {
		sessionStorage.setItem(SESSION_STORAGE_KEY_SESSION_EXPIRED, reason);
	} catch {
		// Storage unavailable: the user just gets the login page without the notice.
	}
}

/**
 * The refresh that is running, shared by every request that got a 403 meanwhile,
 * so N parallel 403s cause one `/auth/refresh` and, if it fails, one logout.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** True while the user's own logout runs: a refresh failing then is not an expired session. */
let explicitLogoutInProgress = false;

interface AuthValues {
	accessToken: string;
	refreshToken: string;
	userId: string;
	orgId: string;
	name: string;
	scopes: string[];
}

interface AuthStore {
	accessToken?: string;
	refreshToken?: string;
	initiated: boolean;
	isLoggedIn: boolean;
	userId?: string;
	orgId?: string;
	loggedInUserName?: string;
	scopes: string[];

	authenticate(
		username: string,
		password: string
	): Promise<
		| User
		| boolean
		| { verificationId: string; ttl: string; phoneNumber: string }
		| { errorCode: string }
		| undefined
	>;
	authenticateSms(code: string, verificationId: string): Promise<User | boolean>;
	/**
	 * Gets a new access token with the refresh token. Concurrent calls share one
	 * request. On failure the user is logged out locally and the login page shows
	 * "Din session har gått ut". Resolves true when the caller can retry.
	 */
	refreshAuthentication(): Promise<boolean>;
	forgotPassword(username: string, orgId?: string): Promise<boolean>;
	passwordReset(newPassword: string, token: string): Promise<boolean>;
	validatePaswordResetToken(token: string): Promise<any>;
	logout(): Promise<void>;
	activateAccount(password: string, token: string): Promise<boolean>;
	initLocalStorage(): Promise<boolean>;
	onLogoutCleanup(): void;
}

function setValues(set: (partial: Partial<AuthStore>) => void, vals: AuthValues) {
	set({
		accessToken: vals.accessToken,
		refreshToken: vals.refreshToken,
		userId: vals.userId,
		orgId: vals.orgId,
		isLoggedIn: true,
		loggedInUserName: vals.name,
		scopes: vals.scopes,
	});
}

function updateLocalStorage(key: string, value?: string) {
	try {
		localStorage.setItem(key, value || "");
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn(`Failed to save ${key} in storage`, err);
	}
}

function setLocalStorageValues(vals: AuthValues) {
	updateLocalStorage(STORAGE_KEY_ACCESS_TOKEN, vals.accessToken);
	updateLocalStorage(STORAGE_KEY_REFRESH_TOKEN, vals.refreshToken);
	updateLocalStorage(STORAGE_KEY_USER_ID, vals.userId);
	updateLocalStorage(STORAGE_KEY_ORGANISATION_ID, vals.orgId);
	updateLocalStorage(STORAGE_KEY_USER_NAME, vals.name);
	updateLocalStorage(STORAGE_KEY_SCOPES, vals.scopes.join(","));
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
	accessToken: undefined,
	refreshToken: undefined,
	initiated: false,
	isLoggedIn: false,
	userId: undefined,
	orgId: undefined,
	loggedInUserName: undefined,
	scopes: [],

	async authenticate(username, password) {
		// Reset any previous state
		get().onLogoutCleanup();

		try {
			const {
				data: { accessToken, refreshToken, user, verificationId, ttl, phone },
			} = await apiStore.post<AuthResponse>({
				path: "/auth/token",
				body: {
					username,
					password,
				},
			});

			if (
				(user && user.roles[0] === USER_TYPE.ORGANISATION_ADMIN) ||
				(user && user.roles[0] === USER_TYPE.ORGANISATION_CONSULTANT_MANAGER)
			) {
				if (accessToken) {
					const vals: AuthValues = {
						accessToken,
						refreshToken,
						userId: user.id,
						orgId: user.profile.organisation.id,
						name: user.firstName + " " + user.lastName,
						scopes: user.scopes || [],
					};
					setValues(set, vals);
					setLocalStorageValues(vals);
					clearSessionExpiredNotice();
				}
			}
			if (user) {
				return user;
			} else if (verificationId) {
				return { verificationId, ttl: ttl?.toString() || "", phoneNumber: phone || "" };
			}
		} catch (error: any) {
			// Surface the auth service's error code so the login page can explain
			// policy failures (e.g. org requires 2FA but the user has no phone)
			// instead of a generic "wrong username/password".
			const errorCode = error?.error?.code;
			return typeof errorCode === "string" ? { errorCode } : false;
		}
	},

	async authenticateSms(code, verificationId) {
		// Reset any previous state
		get().onLogoutCleanup();

		try {
			const {
				data: { accessToken, refreshToken, user },
			} = await apiStore.post<LoginAuthResponse>({
				path: "/auth/token/sms",
				body: {
					code,
					verificationId,
				},
			});

			if (
				(user && user.roles[0] === USER_TYPE.ORGANISATION_ADMIN) ||
				(user && user.roles[0] === USER_TYPE.ORGANISATION_CONSULTANT_MANAGER)
			) {
				if (accessToken) {
					const vals: AuthValues = {
						accessToken,
						refreshToken,
						userId: user.id,
						orgId: user.profile.organisation.id,
						name: user.firstName + " " + user.lastName,
						scopes: user.scopes || [],
					};
					setValues(set, vals);
					setLocalStorageValues(vals);
					clearSessionExpiredNotice();
				}
			}

			return user;
		} catch (error) {
			return false;
		}
	},

	async refreshAuthentication() {
		// Without a refresh token (e.g. already logged out) there is nothing to
		// refresh — bail before hitting the API. This also breaks the logout
		// loop: a 403 on a query that lingers past logout would otherwise
		// trigger refresh → logout() → queryClient.clear() → refetch → 403 → …
		const refreshToken = get().refreshToken;

		if (!refreshToken) {
			return false;
		}

		if (refreshInFlight) {
			return refreshInFlight;
		}

		const refresh = async () => {
			try {
				const {
					data: { accessToken },
				} = await apiStore.post({
					path: "/auth/refresh",
					body: { refreshToken },
					isAuthRequired: false,
				});

				set({ accessToken });
				updateLocalStorage(STORAGE_KEY_ACCESS_TOKEN, accessToken);

				return true;
			} catch (error) {
				// The refresh token was refused (expired by the organisation's session
				// policy, removed, or the call failed): the session is over. The access
				// token was already rejected, so calling /auth/logout with it would only
				// 403 and refresh again; clean up locally instead.
				if (!explicitLogoutInProgress) {
					markSessionExpired(error);
				}
				get().onLogoutCleanup();

				return false;
			}
		};

		refreshInFlight = refresh().finally(() => {
			refreshInFlight = null;
		});

		return refreshInFlight;
	},

	async forgotPassword(username, orgId) {
		try {
			const { status } = await apiStore.post({
				path: "/origo/reset-password",
				body: {
					username: username.trim(),
					organisationId: orgId,
				},
			});

			return status === 200;
		} catch (error) {
			return false;
		}
	},

	async passwordReset(newPassword, token) {
		get().onLogoutCleanup();

		try {
			const { status } = await apiStore.put({
				path: "/reset-password",
				body: {
					newPassword: newPassword.trim(),
					token: token.trim(),
				},
			});

			return status === 202;
		} catch (error) {
			return false;
		}
	},

	async validatePaswordResetToken(token) {
		const result = await apiStore.get({
			path: `/reset-password/${token}`,
		});
		return result;
	},

	async logout() {
		if (!get().isLoggedIn) {
			get().onLogoutCleanup();
			return;
		}

		explicitLogoutInProgress = true;

		try {
			await apiStore.post({
				path: "/auth/logout",
				isAuthRequired: true,
			});
		} catch (err) {
		} finally {
			explicitLogoutInProgress = false;
			get().onLogoutCleanup();
			// The user chose to log out: never show "Din session har gått ut".
			clearSessionExpiredNotice();
		}
	},

	async activateAccount(password, token) {
		get().onLogoutCleanup();

		try {
			await apiStore.put({
				path: "/invitation/" + token,
				body: {
					password,
				},
			});
			return true;
		} catch (error) {
			return false;
		}
	},

	onLogoutCleanup() {
		set({
			accessToken: undefined,
			refreshToken: undefined,
			isLoggedIn: false,
			loggedInUserName: undefined,
			// Also drop the org id so org-scoped queries that stay mounted
			// outside the auth gate (e.g. useOrganisation in ContactContextMenu)
			// disable themselves instead of refetching with a dead token.
			orgId: undefined,
		});
		localStorage.clear();
		// The persisted React Query cache lives in IndexedDB — clear it too so
		// org data doesn't survive a user switch. Fire-and-forget on purpose.
		// eslint-disable-next-line no-console
		clearPersistedCache().catch((err) => console.warn("Failed clearing persisted cache", err));
		// All server state lives in the React Query cache these days
		queryClient.clear();
	},

	/**
	 * Sync stuff persisted in local storage with the in-memory store.
	 */
	async initLocalStorage() {
		try {
			const accessToken = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
			const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN);
			const name = localStorage.getItem(STORAGE_KEY_USER_NAME);
			const orgId = localStorage.getItem(STORAGE_KEY_ORGANISATION_ID);
			const scopes = localStorage.getItem(STORAGE_KEY_SCOPES);

			if (accessToken && refreshToken && orgId && name) {
				const userId = localStorage.getItem(STORAGE_KEY_USER_ID) || "";
				setValues(set, {
					accessToken,
					refreshToken,
					userId,
					orgId,
					name,
					scopes: scopes?.split(",") || [],
				});
			}

			set({ initiated: true });

			return true;
		} catch (err) {
			set({ initiated: true });
			throw err;
		}
	},
}));
